import React from "react";
import { useNavigate } from "react-router-dom";
import { DemoBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Minus } from "lucide-react";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

function lowest(p) {
  const prices = (p.coverage_options || []).map((o) => o.monthly_premium).filter((x) => x != null);
  return prices.length ? Math.min(...prices) : null;
}

const APPLIES_LABEL = { employee: "Employee", spouse: "Spouse/Partner", child: "Child", other_dependent: "Other dependent" };

export function ComparePlans({ products, onElect }) {
  const navigate = useNavigate();
  const elect = onElect || ((id) => navigate(`/app/enroll/${id}`));

  const rows = [
    { label: "Starting premium", render: (p) => <span className="font-display text-lg font-bold text-navy-900">{money(lowest(p))}<span className="text-xs font-sans text-muted-foreground">/mo</span></span> },
    { label: "Carrier", render: (p) => <span className="text-sm text-navy-900">{p.carrier?.name || "—"}</span> },
    { label: "Type", render: (p) => <span className="text-sm text-navy-900">{p.product_type}</span> },
    {
      label: "Coverage options",
      render: (p) => (
        <ul className="space-y-1">
          {(p.coverage_options || []).map((o, i) => (
            <li key={i} className="text-sm text-navy-900 flex justify-between gap-2">
              <span className="text-muted-foreground">{o.label}</span>
              <span className="font-semibold">{money(o.monthly_premium)}</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      label: "Included features",
      render: (p) => (p.riders?.length ? (
        <ul className="space-y-1">
          {p.riders.map((r) => (
            <li key={r} className="text-sm text-navy-900 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-gold-dark" /> {r}</li>
          ))}
        </ul>
      ) : <Minus className="w-4 h-4 text-mist-300" />),
    },
    {
      label: "Who's eligible",
      render: (p) => {
        const applies = p.eligibility?.applies_to || [];
        return applies.length ? (
          <div className="flex flex-wrap gap-1.5">
            {applies.map((a) => <span key={a} className="text-xs bg-mist-100 text-navy-700 px-2 py-0.5 rounded-full">{APPLIES_LABEL[a] || a}</span>)}
          </div>
        ) : <Minus className="w-4 h-4 text-mist-300" />;
      },
    },
    {
      label: "Age range",
      render: (p) => {
        const e = p.eligibility || {};
        if (e.min_age == null && e.max_age == null) return <Minus className="w-4 h-4 text-mist-300" />;
        return <span className="text-sm text-navy-900">{e.min_age ?? 0}–{e.max_age ?? 99}</span>;
      },
    },
    { label: "Benefit limits", render: (p) => <span className="text-sm text-muted-foreground">{p.benefit_limits || "See documents"}</span> },
  ];

  return (
    <div className="overflow-x-auto" data-testid="compare-table">
      <div className="min-w-[640px]">
        {/* Header row */}
        <div className="grid" style={{ gridTemplateColumns: `160px repeat(${products.length}, minmax(180px, 1fr))` }}>
          <div />
          {products.map((p) => (
            <div key={p.id} className="px-4 pb-4 border-b-2 border-navy-800">
              <div className="flex items-center gap-2 mb-1"><DemoBadge /></div>
              <h4 className="font-display text-lg font-bold text-navy-900 leading-tight">{p.name}</h4>
            </div>
          ))}
        </div>

        {rows.map((row, ri) => (
          <div key={ri} className="grid items-start" style={{ gridTemplateColumns: `160px repeat(${products.length}, minmax(180px, 1fr))` }}>
            <div className="px-2 py-4 text-xs font-bold uppercase tracking-wider text-navy-600 border-b border-mist-200">{row.label}</div>
            {products.map((p) => (
              <div key={p.id} className="px-4 py-4 border-b border-mist-200 bg-white/40">{row.render(p)}</div>
            ))}
          </div>
        ))}

        {/* Action row */}
        <div className="grid" style={{ gridTemplateColumns: `160px repeat(${products.length}, minmax(180px, 1fr))` }}>
          <div />
          {products.map((p) => (
            <div key={p.id} className="px-4 pt-4">
              <Button onClick={() => elect(p.id)} className="w-full bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid={`compare-elect-${p.id}`}>
                Elect
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
